import { useMemo } from 'react';

import { Environment } from '@/react/portainer/environments/types';
import { EnvironmentGroup } from '@/react/portainer/environments/environment-groups/types';

import { BadgeType } from '@@/Badge';

import { Policy } from '../../types';
import { isEnvironmentSupportedByPolicy } from '../../utils/isEnvironmentTypeSupportedByPolicy';

import { EnrichedPolicy } from './types';

export function usePoliciesWithEnvironmentGroups(
  policies: Policy[],
  groups: EnvironmentGroup[],
  environments: Environment[]
): EnrichedPolicy[] {
  return useMemo(
    () =>
      policies.map((policy) => ({
        ...policy,
        environmentGroups: computeEnvironmentGroups(
          policy,
          groups,
          environments
        ),
        status: computeStatus(policy),
      })),
    [policies, groups, environments]
  );
}

function computeEnvironmentGroups(
  policy: Policy,
  groups: EnvironmentGroup[],
  environments: Environment[]
) {
  if (policy.EnvironmentGroups?.length === 0) {
    return [];
  }

  return (
    groups
      .filter((group) => policy.EnvironmentGroups?.includes(group.Id))
      .map((group) => {
        const environmentsInGroup = environments.filter(
          (environment) => environment.GroupId === group.Id
        );
        const isEnvironmentGroupFullySupported = environmentsInGroup.every(
          (environment) =>
            isEnvironmentSupportedByPolicy(environment, policy.Type)
        );

        const status: BadgeType = isEnvironmentGroupFullySupported
          ? 'info'
          : 'warn';

        return {
          name: group.Name,
          id: group.Id,
          status,
          statusMessage: isEnvironmentGroupFullySupported
            ? ''
            : 'This environment group contains unsupported environment(s) for this policy',
        };
      })
      // show the env groups with unsupported environments for the policy first
      .sort((a, b) => {
        if (a.status === 'warn' && b.status !== 'warn') {
          return -1;
        }
        if (a.status !== 'warn' && b.status === 'warn') {
          return 1;
        }
        return 0;
      })
  );
}

function computeStatus(policy: Policy): 'Active' | 'Inactive' {
  const isActive =
    policy.EnvironmentGroups && policy.EnvironmentGroups.length > 0;
  return isActive ? 'Active' : 'Inactive';
}
